import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import Dayjs from 'dayjs'
import React from 'react'

import {
  capexDecision,
  fetchCapexBoard,
  fetchCapexBudgets,
  fetchCapexListItems,
  fetchCapexSheet,
  fetchMyCentres,
  saveCapexBudgets,
  saveCapexListItems,
  searchCatalogue
} from 'api/procurement'
import constants from 'app/constants'
import { confirmDialog, eventAlert } from 'app/helpers'

import { META, StatusChip } from './HqMonthlyBoard'

// Per-line receipt wording (centre sets these as capex items arrive).
const RECEIPT_LABEL = {
  pending: 'Awaiting',
  received: 'Received',
  not_received: 'Not received',
  discrepancy: 'Discrepancy',
  closed: 'Closed by HQ',
  unavailable: 'Unavailable'
}
const FLAGGED = ['not_received', 'discrepancy']

import { capexOrderSheet, capexOrderSpec } from '../components/capexPo'
import { errorText } from '../components/errorText'
import {
  downloadOrderPdf,
  downloadOrderPdfSections,
  downloadXlsxSheets
} from '../components/exportUtils'
import { defaultOrderMonth, orderCentres } from '../constants'

// HQ side of the Monthly Capex cycle, three sub-tabs (user 2026-08-22):
// Month setup (the curated item list), Budgets (per-centre ₹), Review (the
// all-centres board, same shape as the Monthly Cadence board). Tables follow
// the centre-page idioms — sticky header rows, search boxes and action buttons
// IN them, and an ⓘ that explains the page.
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

const useStyles = makeStyles((theme) => ({
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  spacer: { flex: 1 },
  fitTable: { tableLayout: 'fixed', width: '100%', background: '#fff' },
  // Sticky header row — search boxes + the action button live in it, and it
  // stays frozen while the rows scroll (user 2026-08-22).
  headCell: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: '#fff',
    borderBottom: '2px solid #ddd'
  },
  wrap: { wordBreak: 'break-word', whiteSpace: 'normal' },
  muted: { color: theme.palette.text.secondary },
  // Divider row introducing the catalogue matches the header search surfaces
  // beneath the month's own (filtered) rows.
  addHeader: {
    color: theme.palette.text.secondary,
    fontWeight: 650,
    textTransform: 'lowercase',
    background: theme.palette.grey[50],
    borderTop: '2px solid #ddd'
  },
  totalRow: { '& td': { fontWeight: 700, borderTop: '2px solid #ddd' } },
  // Board idioms copied from the Monthly Cadence board so the two HQ views
  // read identically: page-level freeze panes (header sticks on scroll-down,
  // Sl No. + Centre stick on scroll-right) + zebra painted on the CELLS so the
  // frozen columns carry the band.
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  boardCell: { padding: '9px 12px' },
  // PO numbers (HRBR/X/2026-12/b5247d) must stay on one line.
  poCell: { whiteSpace: 'nowrap' },
  receiptNote: {
    fontSize: 11,
    color: '#5e35b1',
    marginTop: 2,
    whiteSpace: 'nowrap'
  },
  // HQ marked lines unavailable at order time: what the centre asked for.
  unavailNote: { fontSize: 11, color: '#8a3d00', marginTop: 2 },
  askedTotal: {
    fontSize: 11,
    color: '#757575',
    marginTop: 2,
    textDecoration: 'line-through'
  },
  unavailChip: {
    marginTop: 4,
    height: 20,
    fontSize: 11,
    fontWeight: 600,
    background: '#fff1e0',
    color: '#8a3d00'
  },
  centreName: { fontWeight: 650, fontSize: 14 },
  stickyTable: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100],
      boxShadow: 'inset 0 -1px 0 #e0ddd6'
    },
    '& td': { boxShadow: 'inset 0 -1px 0 #eceae4' },
    '& tbody tr:nth-of-type(odd) td': {
      backgroundColor: theme.palette.grey[50]
    },
    '& tbody tr:nth-of-type(even) td': {
      backgroundColor: theme.palette.background.paper
    },
    '& tbody tr:hover td': {
      backgroundColor: theme.palette.action.hover
    }
  },
  frz1: { position: 'sticky', left: 0, zIndex: 4, width: 60, minWidth: 60 },
  frz2: {
    position: 'sticky',
    left: 60,
    zIndex: 4,
    boxShadow: 'inset 0 -1px 0 #eceae4, 8px 0 8px -8px rgba(0,0,0,.15)'
  },
  frzHead1: { position: 'sticky', left: 0, top: 0, zIndex: 7 },
  frzHead2: {
    position: 'sticky',
    left: 60,
    top: 0,
    zIndex: 7,
    boxShadow: 'inset 0 -1px 0 #e0ddd6, 8px 0 8px -8px rgba(0,0,0,.15)'
  }
}))

// The item's product page. HQ gets it as its own column (user 2026-08-25) —
// on the month setup list and on the line detail behind View — because HQ is
// who places the order and the link is what they order from.
const ItemLink = ({ link }) =>
  link ? (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      title="Open this item's product page"
      style={{ fontSize: 12, whiteSpace: 'nowrap' }}
    >
      view ↗
    </a>
  ) : (
    <span style={{ opacity: 0.45 }}>—</span>
  )

// ------------------------------------------------------------------- ⓘ
const CapexInfo = ({ title, lines }) => {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <IconButton
        size="small"
        title="How this page works"
        onClick={() => setOpen(true)}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            component="ol"
            style={{ paddingLeft: 18 }}
          >
            {lines.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// ------------------------------------------------------------------ setup
export const HqCapexSetup = () => {
  const classes = useStyles()
  const [month, setMonth] = React.useState(defaultOrderMonth())
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  // The staged item list; publish replaces the month's set wholesale.
  const [items, setItems] = React.useState([])
  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState([])

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchCapexListItems(month)
      setItems(
        (res.items || []).map((it) => ({ ...it, uuid: it.catalogue_item }))
      )
    } catch (error) {
      eventAlert(
        errorText(error, 'Failed to load the month'),
        constants.ERROR,
        'Capex'
      )
    } finally {
      setLoading(false)
    }
  }, [month])

  React.useEffect(() => {
    load()
  }, [load])

  const runSearch = async (text) => {
    setQuery(text)
    if (!text.trim()) {
      setResults([])
      return
    }
    try {
      const res = await searchCatalogue(text)
      setResults(res?.results || [])
    } catch (error) {
      setResults([])
    }
  }

  const publishItems = async () => {
    setSaving(true)
    try {
      const res = await saveCapexListItems(
        month,
        items.map((it) => it.uuid)
      )
      eventAlert(
        `Published — ${res.count} item${
          res.count === 1 ? '' : 's'
        } on the ${monthLabel} list`,
        constants.SUCCESS,
        'Capex'
      )
    } catch (error) {
      eventAlert(errorText(error, 'Publish failed'), constants.ERROR, 'Capex')
    } finally {
      setSaving(false)
    }
  }

  const monthLabel = Dayjs(`${month}-01`).format('MMMM YYYY')
  const itemCostSum = items.reduce((s, it) => s + Number(it.cost || 0), 0)
  const f = query.trim().toLowerCase()
  const shown = items.filter(
    (it) =>
      !f ||
      it.name.toLowerCase().includes(f) ||
      (it.reference_id || '').toLowerCase().includes(f) ||
      (it.category || '').toLowerCase().includes(f)
  )
  // Catalogue matches not already on the list — offered as add rows under the
  // same search (one box searches both, user 2026-08-24).
  const addable = f
    ? results.filter((r) => !items.some((p) => p.uuid === r.uuid))
    : []
  // Sl No. is the item's place in the PUBLISHED list, not its place in the
  // filtered view — so a number HQ quotes still means the same row after a
  // search. Under a search the numbers simply go non-contiguous.
  const slOf = new Map(items.map((it, i) => [it.uuid, i + 1]))

  return (
    <div>
      <div className={classes.topRow}>
        <TextField
          type="month"
          size="small"
          variant="outlined"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <CapexInfo
          title="Month setup"
          lines={[
            `The table's search box finds both the ${monthLabel} list and catalogue items to add.`,
            'Publish replaces the month’s list for every centre at once.',
            'Link opens the item’s product page — the same link rides into the PO downloads.',
            'Set each centre’s ₹ limit on the Budgets tab — centres can’t submit without one.',
            'Centres fill their quantities during the order window; submissions land in Review.'
          ]}
        />
        {loading && <CircularProgress size={20} />}
        <span className={classes.spacer} />
        <Typography variant="body2" className={classes.muted}>
          {monthLabel} — {items.length} item{items.length === 1 ? '' : 's'}
          {items.length ? ` · ${inr(itemCostSum)} at 1 pc each` : ''}
        </Typography>
      </div>

      <Paper variant="outlined">
        <Table size="small" className={classes.fitTable}>
          <TableHead>
            <TableRow>
              <TableCell width="6%" className={classes.headCell}>
                Sl No.
              </TableCell>
              <TableCell width="32%" className={classes.headCell}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder={`Search the ${monthLabel} list + the catalogue…`}
                  value={query}
                  onChange={(e) => runSearch(e.target.value)}
                  fullWidth
                />
              </TableCell>
              <TableCell width="14%" className={classes.headCell}>
                Reference ID
              </TableCell>
              <TableCell width="14%" className={classes.headCell}>
                Category
              </TableCell>
              <TableCell width="8%" className={classes.headCell}>
                Link
              </TableCell>
              <TableCell width="9%" className={classes.headCell}>
                Cost/pc
              </TableCell>
              <TableCell width="17%" className={classes.headCell}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={saving || !items.length}
                  onClick={publishItems}
                >
                  Publish list
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((it) => (
              <TableRow key={it.uuid} hover>
                <TableCell className={classes.muted}>
                  {slOf.get(it.uuid)}
                </TableCell>
                <TableCell className={classes.wrap}>{it.name}</TableCell>
                <TableCell className={classes.muted}>
                  {it.reference_id || '—'}
                </TableCell>
                <TableCell className={`${classes.muted} ${classes.wrap}`}>
                  {it.category || '—'}
                </TableCell>
                <TableCell>
                  <ItemLink link={it.reference_link} />
                </TableCell>
                <TableCell>{it.cost != null ? inr(it.cost) : '—'}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    title="Remove from the month's list"
                    onClick={() =>
                      setItems((prev) => prev.filter((p) => p.uuid !== it.uuid))
                    }
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {addable.length > 0 && (
              <TableRow>
                <TableCell colSpan={7} className={classes.addHeader}>
                  Add from the catalogue
                </TableCell>
              </TableRow>
            )}
            {addable.map((it) => (
              <TableRow key={it.uuid} hover>
                <TableCell className={classes.muted}>—</TableCell>
                <TableCell className={classes.wrap}>{it.name}</TableCell>
                <TableCell className={classes.muted}>
                  {it.reference_id || '—'}
                </TableCell>
                <TableCell className={`${classes.muted} ${classes.wrap}`}>
                  {it.category || '—'}
                </TableCell>
                <TableCell>
                  <ItemLink link={it.reference_link} />
                </TableCell>
                <TableCell>{it.cost != null ? inr(it.cost) : '—'}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setItems((prev) =>
                        prev.some((p) => p.uuid === it.uuid)
                          ? prev
                          : [...prev, it]
                      )
                    }
                  >
                    Add
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!shown.length && !addable.length && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="textSecondary">
                    {items.length
                      ? 'Nothing on the list or in the catalogue matches the search.'
                      : `No items on the ${monthLabel} list yet — the search above finds catalogue items to add. Publish when the list is ready.`}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  )
}

// ----------------------------------------------------------------- budgets
export const HqCapexBudgets = () => {
  const classes = useStyles()
  const [month, setMonth] = React.useState(defaultOrderMonth())
  const [saving, setSaving] = React.useState(false)
  const [centres, setCentres] = React.useState([])
  const [amounts, setAmounts] = React.useState({}) // centre uuid -> string
  const [qCentre, setQCentre] = React.useState('')

  React.useEffect(() => {
    ;(async () => {
      try {
        const res = await fetchMyCentres()
        setCentres(orderCentres(res?.results || []))
      } catch (error) {
        eventAlert(
          errorText(error, 'Failed to load centres'),
          constants.ERROR,
          'Capex'
        )
      }
    })()
  }, [])

  React.useEffect(() => {
    ;(async () => {
      try {
        const res = await fetchCapexBudgets(month)
        const a = {}
        ;(res.budgets || []).forEach((b) => {
          a[b.centre] = String(b.amount)
        })
        setAmounts(a)
      } catch (error) {
        eventAlert(
          errorText(error, 'Failed to load budgets'),
          constants.ERROR,
          'Capex'
        )
      }
    })()
  }, [month])

  const saveBudgets = async () => {
    setSaving(true)
    try {
      const budgets = centres
        .filter((c) => String(amounts[c.uuid] ?? '').trim() !== '')
        .map((c) => ({ centre: c.uuid, amount: Number(amounts[c.uuid]) }))
      const res = await saveCapexBudgets(month, budgets)
      eventAlert(
        `Saved budgets for ${res.count} centre${res.count === 1 ? '' : 's'}`,
        constants.SUCCESS,
        'Capex'
      )
    } catch (error) {
      eventAlert(
        errorText(error, 'Saving budgets failed'),
        constants.ERROR,
        'Capex'
      )
    } finally {
      setSaving(false)
    }
  }

  const copyPrevious = async () => {
    const prev = Dayjs(`${month}-01`).subtract(1, 'month').format('YYYY-MM')
    try {
      const res = await fetchCapexBudgets(prev)
      const rows = res.budgets || []
      if (!rows.length) {
        eventAlert(
          `No budgets on ${Dayjs(`${prev}-01`).format('MMMM YYYY')} to copy.`,
          constants.ERROR,
          'Capex'
        )
        return
      }
      const a = {}
      rows.forEach((b) => {
        a[b.centre] = String(b.amount)
      })
      setAmounts(a)
      eventAlert(
        `Copied ${rows.length} budgets — review, then Save.`,
        constants.SUCCESS,
        'Capex'
      )
    } catch (error) {
      eventAlert(errorText(error, 'Copy failed'), constants.ERROR, 'Capex')
    }
  }

  const monthLabel = Dayjs(`${month}-01`).format('MMMM YYYY')
  const budgetSum = centres.reduce(
    (s, c) => s + (Number(amounts[c.uuid]) || 0),
    0
  )
  const f = qCentre.trim().toLowerCase()
  const shown = centres.filter((c) => !f || c.name.toLowerCase().includes(f))

  return (
    <div>
      <div className={classes.topRow}>
        <TextField
          type="month"
          size="small"
          variant="outlined"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button size="small" variant="outlined" onClick={copyPrevious}>
          Copy from {Dayjs(`${month}-01`).subtract(1, 'month').format('MMM')}
        </Button>
        <CapexInfo
          title="Centre budgets"
          lines={[
            `Each centre's ₹ limit for its ${monthLabel} capex request.`,
            'A centre cannot submit without a budget, and cannot submit above it.',
            'Blank leaves a centre without a budget — it can draft but not submit.',
            'Save applies the filled rows; other months are untouched.'
          ]}
        />
        <span className={classes.spacer} />
        <Typography variant="body2" className={classes.muted}>
          {monthLabel}
          {budgetSum ? ` — ${inr(budgetSum)} total` : ''}
        </Typography>
      </div>

      <Paper variant="outlined" style={{ maxWidth: 560 }}>
        <Table size="small" className={classes.fitTable}>
          <TableHead>
            <TableRow>
              <TableCell width="55%" className={classes.headCell}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Centre — search"
                  value={qCentre}
                  onChange={(e) => setQCentre(e.target.value)}
                  fullWidth
                />
              </TableCell>
              <TableCell width="45%" className={classes.headCell}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={saving || !centres.length}
                  onClick={saveBudgets}
                >
                  Save budgets
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((c) => (
              <TableRow key={c.uuid} hover>
                <TableCell>{c.name}</TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    variant="outlined"
                    placeholder="₹"
                    value={amounts[c.uuid] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value
                      setAmounts((prev) => ({ ...prev, [c.uuid]: v }))
                    }}
                    inputProps={{ min: 0, style: { width: 90 } }}
                  />
                </TableCell>
              </TableRow>
            ))}
            {!shown.length && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="textSecondary">
                    {centres.length
                      ? 'No centre matches the search.'
                      : 'Loading centres…'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  )
}

// ----------------------------------------------------------------- review
// The all-centres board (user 2026-08-22): mirrors the Monthly Cadence board —
// one row per centre with its month status, so HQ sees who has NOT filled, not
// just who submitted, and acts from the row (approve / send back / reopen).
// Downloads are purchase orders built by the shared capexPo builder — the same
// document the centre downloads — with the item's catalogue link in it.

export const HqCapexReview = () => {
  const classes = useStyles()
  const [month, setMonth] = React.useState(defaultOrderMonth())
  const [rows, setRows] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [busyId, setBusyId] = React.useState(null)
  const [menu, setMenu] = React.useState(null) // {el, row}
  const [view, setView] = React.useState(null)
  // HQ note that rides along with Reorder / Close on flagged receipt lines.
  const [issueNote, setIssueNote] = React.useState('') // {row, sheet}
  const [reject, setReject] = React.useState(null) // row
  const [rejectRemarks, setRejectRemarks] = React.useState('')
  const [acting, setActing] = React.useState(false)
  const [exporting, setExporting] = React.useState(false)
  // Mark ordered, per item (user 2026-10-22): {row, lines} — HQ flips an
  // item it could not buy to Unavailable with a reason the centre sees.
  const [ordering, setOrdering] = React.useState(null)

  const monthLabel = Dayjs(`${month}-01`).format('MMMM YYYY')

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchCapexBoard(month)
      setRows(res.results || [])
    } catch (error) {
      // Local vite preview only: the board endpoint 404s until the backend
      // deploys — compose the same rows from the per-centre sheet endpoint.
      // import.meta.env.DEV is false in every build, so this is dead in prod.
      if (import.meta.env.DEV) {
        try {
          const cs = orderCentres((await fetchMyCentres())?.results || [])
          const sheets = await Promise.all(
            cs.map((c) => fetchCapexSheet(c.uuid, month).catch(() => null))
          )
          setRows(
            cs.map((c, i) => {
              const s = sheets[i] || {}
              const lines = (s.items || []).filter((it) => Number(it.qty) > 0)
              const total = lines.reduce(
                (t, it) => t + Number(it.line_total || 0),
                0
              )
              return {
                centre: c.uuid,
                centre_name: c.name,
                status:
                  s.status === 'draft' && !lines.length
                    ? 'not_filled'
                    : s.status || 'not_filled',
                po_number: s.po_number || '',
                submitted_at: null,
                items: lines.length,
                total,
                budget: s.budget != null ? s.budget : null,
                submitted_by: ''
              }
            })
          )
        } catch (e2) {
          setRows([])
        }
      } else {
        eventAlert(
          errorText(error, 'Failed to load the board'),
          constants.ERROR,
          'Capex'
        )
        setRows([])
      }
    } finally {
      setLoading(false)
    }
  }, [month])

  React.useEffect(() => {
    load()
  }, [load])

  const list = rows || []
  const notFilled = list.filter((r) => r.status === 'not_filled').length
  const waiting = list.filter((r) => r.status === 'submitted').length
  const withUnavailable = list.filter((r) => Number(r.unavailable_lines) > 0)
  const unavailableTotal = withUnavailable.reduce(
    (t, r) => t + Number(r.unavailable_total || 0),
    0
  )

  const act = async (r, action, remarks) => {
    setActing(true)
    setBusyId(r.centre)
    try {
      await capexDecision({
        centre: r.centre,
        month,
        action,
        remarks: remarks || ''
      })
      eventAlert(
        action === 'approve'
          ? `${r.centre_name} approved`
          : action === 'reject'
          ? `${r.centre_name} sent back to the centre`
          : `${r.centre_name} reopened`,
        constants.SUCCESS,
        'Capex'
      )
      setReject(null)
      setRejectRemarks('')
      setView(null)
      load()
    } catch (error) {
      eventAlert(errorText(error, 'Action failed'), constants.ERROR, 'Capex')
    } finally {
      setActing(false)
      setBusyId(null)
    }
  }

  // Approve / reopen confirm first; Send back and Mark ordered go through
  // their own dialogs.
  const confirmAct = async (r, action) => {
    const verb = action === 'approve' ? 'Approve' : 'Reopen for the centre'
    const { isConfirmed } = await confirmDialog({
      text: `${verb} — Monthly Capex for ${r.centre_name} (${monthLabel})?`,
      confirmButtonText: 'Yes',
      options: { title: 'Please confirm' }
    })
    if (isConfirmed) act(r, action)
  }

  // Resolve flagged receipt lines (centre said not received / discrepancy):
  // 'reorder' sends them back to awaiting so the centre re-confirms;
  // 'close_issue' accepts them as-is. lines = order uuids; [] = all flagged.
  const resolveLines = async (action, lines) => {
    if (!view) return
    const n = lines.length || Number(view.sheet.flagged_lines || 0)
    const { isConfirmed } = await confirmDialog({
      text:
        action === 'reorder'
          ? `Reorder ${n} flagged item${n === 1 ? '' : 's'} for ${
              view.row.centre_name
            }? They go back to "awaiting" and the centre is asked to confirm receipt again.`
          : `Close the issue on ${n} flagged item${n === 1 ? '' : 's'} for ${
              view.row.centre_name
            }? No reorder — the centre is told the shortfall stands.`,
      confirmButtonText: 'Yes',
      options: { title: 'Please confirm' }
    })
    if (!isConfirmed) return
    setActing(true)
    try {
      const res = await capexDecision({
        centre: view.row.centre,
        month,
        action,
        remarks: issueNote,
        lines
      })
      eventAlert(
        action === 'reorder'
          ? `${res.acted} item${
              res.acted === 1 ? '' : 's'
            } reordered — centre notified`
          : `Issue closed on ${res.acted} item${
              res.acted === 1 ? '' : 's'
            } — centre notified`,
        constants.SUCCESS,
        'Capex'
      )
      setIssueNote('')
      const sheet = await fetchCapexSheet(view.row.centre, month)
      setView((v) => (v ? { ...v, sheet } : v))
      load()
    } catch (error) {
      eventAlert(errorText(error, 'Action failed'), constants.ERROR, 'Capex')
    } finally {
      setActing(false)
    }
  }

  // Mark ordered = HQ has placed the order: the month shows Ordered and the
  // centre's "Check & confirm receipt" step opens. Every line starts as
  // Ordered; one HQ could not buy is set Unavailable with a reason — it
  // leaves the spend and the centre's receipt checklist.
  const openOrder = async (row) => {
    try {
      const sheet =
        view && view.row === row
          ? view.sheet
          : await fetchCapexSheet(row.centre, month)
      setOrdering({
        row,
        lines: (sheet.items || [])
          .filter((it) => Number(it.qty) > 0)
          .map((it) => ({
            id: it.order || it.catalogue_item,
            name: it.name,
            qty: Number(it.qty),
            total: Number(it.line_total || 0),
            unavailable: false,
            note: ''
          }))
      })
    } catch (error) {
      eventAlert(
        errorText(error, 'Failed to load the sheet'),
        constants.ERROR,
        'Capex'
      )
    }
  }

  const updateOrderLine = (i, patch) =>
    setOrdering((o) => ({
      ...o,
      lines: o.lines.map((l, j) => (j === i ? { ...l, ...patch } : l))
    }))

  const submitOrder = async () => {
    const { row, lines } = ordering
    const un = lines.filter((l) => l.unavailable)
    if (un.some((l) => !l.note.trim())) {
      eventAlert(
        'Add a reason for each unavailable item',
        constants.WARNING,
        'Capex'
      )
      return
    }
    setActing(true)
    try {
      await capexDecision({
        centre: row.centre,
        month,
        action: 'order',
        remarks: '',
        unavailable: un.map((l) => ({ id: l.id, note: l.note.trim() }))
      })
      eventAlert(
        un.length
          ? `${row.centre_name} marked ordered — ${un.length} item${
              un.length === 1 ? '' : 's'
            } unavailable, centre notified`
          : `${row.centre_name} marked ordered — the centre can now confirm receipt`,
        constants.SUCCESS,
        'Capex'
      )
      setOrdering(null)
      setView(null)
      load()
    } catch (error) {
      eventAlert(errorText(error, 'Action failed'), constants.ERROR, 'Capex')
    } finally {
      setActing(false)
    }
  }

  const openView = async (row) => {
    try {
      const sheet = await fetchCapexSheet(row.centre, month)
      setIssueNote('')
      setView({ row, sheet })
    } catch (error) {
      eventAlert(
        errorText(error, 'Failed to load the sheet'),
        constants.ERROR,
        'Capex'
      )
    }
  }

  // ---- PO exports ----
  // Everything a centre's order document needs, in the shape capexPo expects.
  const poOptsFor = (r, sheet) => ({
    lines: (sheet.items || []).filter((it) => Number(it.qty) > 0),
    centreName: r.centre_name,
    sheetName: r.centre_name,
    monthLabel,
    po: r.po_number || '',
    budget: r.budget != null ? r.budget : null,
    submittedAt: r.submitted_at || null,
    submittedBy: r.submitted_by || ''
  })

  const exportRow = async (r, kind) => {
    try {
      const sheet =
        view && view.row === r
          ? view.sheet
          : await fetchCapexSheet(r.centre, month)
      const opts = poOptsFor(r, sheet)
      const fname = `Capex-PO-${r.centre_name.replace(/\s+/g, '-')}-${month}`
      if (kind === 'excel') {
        downloadXlsxSheets(fname, [capexOrderSheet(opts)])
      } else {
        downloadOrderPdf(fname, capexOrderSpec(opts))
      }
    } catch (error) {
      eventAlert(errorText(error, 'Export failed'), constants.ERROR, 'Capex')
    }
  }

  // Board download: a Summary, then each filled centre as its own PO.
  const runExport = async (kind) => {
    setExporting(true)
    try {
      const filled = list.filter((r) => Number(r.items) > 0)
      const sheets = await Promise.all(
        filled.map((r) => fetchCapexSheet(r.centre, month))
      )
      const summary = {
        headers: [
          'Sl No.',
          'Centre',
          'PO',
          'Items',
          'Total (Rs)',
          'Budget (Rs)',
          'Status'
        ],
        rows: list.map((r, i) => [
          i + 1,
          r.centre_name,
          r.po_number || '',
          Number(r.items || 0),
          Math.round(Number(r.total || 0)),
          r.budget != null ? Number(r.budget) : '',
          (META[r.status === 'sent' ? 'ordered' : r.status] || {}).label ||
            r.status
        ])
      }
      const fname = `Capex-${month}`
      if (kind === 'excel') {
        downloadXlsxSheets(fname, [
          { name: 'Summary', headers: summary.headers, rows: summary.rows },
          ...filled.map((r, i) => capexOrderSheet(poOptsFor(r, sheets[i])))
        ])
      } else {
        downloadOrderPdfSections(fname, [
          {
            title: `Monthly Capex — ${monthLabel} · Summary`,
            headers: summary.headers,
            rows: summary.rows,
            signoff: false
          },
          ...filled.map((r, i) => capexOrderSpec(poOptsFor(r, sheets[i])))
        ])
      }
    } catch (error) {
      eventAlert(
        errorText(error, 'Could not build the export'),
        constants.ERROR,
        'Capex'
      )
    } finally {
      setExporting(false)
    }
  }

  const hasMenu = (r) => r.status === 'submitted' || r.status === 'approved'

  return (
    <div>
      <div className={classes.bar}>
        <TextField
          type="month"
          size="small"
          variant="outlined"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {rows && (
          <>
            <Chip
              size="small"
              label={`${notFilled} not filled`}
              style={{
                background: 'transparent',
                border: '1px dashed #c9791f',
                color: '#c9791f',
                fontWeight: 600
              }}
            />
            <Chip
              size="small"
              label={`${waiting} waiting for HQ`}
              style={{
                background: '#fff8e1',
                color: '#8d6e00',
                fontWeight: 600
              }}
            />
            {withUnavailable.length ? (
              <Chip
                size="small"
                label={`${
                  withUnavailable.length
                } with unavailable items · ${inr(unavailableTotal)}`}
                style={{
                  background: '#fff1e0',
                  color: '#8a3d00',
                  fontWeight: 600
                }}
              />
            ) : null}
          </>
        )}
        <CapexInfo
          title="Review"
          lines={[
            'Every centre appears with its month status — not filled, draft, waiting for HQ, approved or sent back.',
            'View opens the line detail; downloads are purchase orders (a Summary plus one PO per filled centre), each line carrying the item’s catalogue link.',
            'Approve locks the month in; Send back reopens it for the centre with your note; Reopen unlocks an approved month; Mark ordered tells the centre the order is placed so they can confirm receipt as items arrive.',
            'In Mark ordered, switch an item you could not buy to Unavailable with a reason: the centre sees the reason, it drops out of their receipt checklist, and Items / Total show what was actually ordered (the centre’s ask struck through). Reorder on that line brings it back if it comes in stock.',
            'Pick any month above — decided months stay on the board.'
          ]}
        />
        <span className={classes.spacer} />
        <Button
          size="small"
          variant="outlined"
          disabled={exporting || !list.length}
          onClick={() => runExport('excel')}
        >
          ⬇ Excel
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={exporting || !list.length}
          style={{ marginLeft: 8 }}
          onClick={() => runExport('pdf')}
        >
          ⬇ PDF
        </Button>
      </div>

      {loading && <LinearProgress />}
      {rows && (
        <Paper variant="outlined" style={{ overflow: 'visible' }}>
          <Table size="small" className={classes.stickyTable}>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${classes.boardCell} ${classes.frzHead1}`}
                >
                  Sl No.
                </TableCell>
                <TableCell
                  className={`${classes.boardCell} ${classes.frzHead2}`}
                >
                  Centre
                </TableCell>
                <TableCell className={classes.boardCell}>PO</TableCell>
                <TableCell className={classes.boardCell}>Month</TableCell>
                <TableCell className={classes.boardCell}>Items</TableCell>
                <TableCell className={classes.boardCell}>Total</TableCell>
                <TableCell className={classes.boardCell}>Budget</TableCell>
                <TableCell className={classes.boardCell}>Status</TableCell>
                <TableCell className={classes.boardCell}>
                  Submitted by
                </TableCell>
                <TableCell className={classes.boardCell}>View</TableCell>
                <TableCell className={classes.boardCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((r, idx) => (
                <TableRow key={r.centre} hover>
                  <TableCell className={`${classes.boardCell} ${classes.frz1}`}>
                    {idx + 1}
                  </TableCell>
                  <TableCell className={`${classes.boardCell} ${classes.frz2}`}>
                    <span className={classes.centreName}>{r.centre_name}</span>
                  </TableCell>
                  <TableCell
                    className={`${classes.boardCell} ${classes.poCell}`}
                  >
                    {r.po_number || '—'}
                  </TableCell>
                  <TableCell className={classes.boardCell}>
                    {monthLabel}
                  </TableCell>
                  <TableCell className={classes.boardCell}>
                    {Number(r.items || 0) || '—'}
                    {Number(r.unavailable_lines) > 0 ? (
                      <div className={classes.unavailNote}>
                        of {Number(r.asked_items || 0)} asked
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className={classes.boardCell}>
                    {inr(r.total)}
                    {Number(r.unavailable_lines) > 0 ? (
                      <div className={classes.askedTotal}>
                        {inr(r.asked_total)}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className={classes.boardCell}>
                    {r.budget != null ? inr(r.budget) : '—'}
                  </TableCell>
                  <TableCell className={classes.boardCell}>
                    <StatusChip
                      s={r.status === 'sent' ? 'ordered' : r.status}
                    />
                    {r.status === 'sent' || r.status === 'received' ? (
                      <div
                        className={classes.receiptNote}
                        title={r.discrepancy_notes || ''}
                      >
                        {r.status === 'sent'
                          ? `${Number(r.received_lines || 0)}/${Number(
                              r.items || 0
                            )} received`
                          : 'all received'}
                        {r.discrepancy ? ' · ⚠ issues' : ''}
                      </div>
                    ) : null}
                    {Number(r.unavailable_lines) > 0 ? (
                      <Chip
                        size="small"
                        className={classes.unavailChip}
                        label={`${r.unavailable_lines} unavailable`}
                        title="Not ordered — open View for the items and reasons"
                      />
                    ) : null}
                  </TableCell>
                  <TableCell className={classes.boardCell}>
                    {r.submitted_by || '—'}
                  </TableCell>
                  <TableCell className={classes.boardCell}>
                    <Button size="small" onClick={() => openView(r)}>
                      View
                    </Button>
                  </TableCell>
                  <TableCell className={classes.boardCell}>
                    {hasMenu(r) && (
                      <IconButton
                        size="small"
                        disabled={busyId === r.centre}
                        title="Actions"
                        onClick={(e) =>
                          setMenu({ el: e.currentTarget, row: r })
                        }
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!list.length && (
                <TableRow>
                  <TableCell colSpan={11} className={classes.boardCell}>
                    <Typography variant="body2" color="textSecondary">
                      No centres to show.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {menu && (
        <Menu anchorEl={menu.el} open onClose={() => setMenu(null)}>
          {menu.row.status === 'submitted' && (
            <MenuItem
              onClick={() => {
                confirmAct(menu.row, 'approve')
                setMenu(null)
              }}
            >
              Approve
            </MenuItem>
          )}
          {menu.row.status === 'submitted' && (
            <MenuItem
              style={{ color: '#c62828' }}
              onClick={() => {
                setReject(menu.row)
                setMenu(null)
              }}
            >
              Send back…
            </MenuItem>
          )}
          {menu.row.status === 'approved' && (
            <MenuItem
              onClick={() => {
                openOrder(menu.row)
                setMenu(null)
              }}
            >
              Mark ordered
            </MenuItem>
          )}
          {menu.row.status === 'approved' && (
            <MenuItem
              onClick={() => {
                confirmAct(menu.row, 'reopen')
                setMenu(null)
              }}
            >
              Reopen for the centre
            </MenuItem>
          )}
        </Menu>
      )}

      <Dialog
        open={!!view}
        onClose={() => setView(null)}
        maxWidth="md"
        fullWidth
      >
        {view && (
          <>
            <DialogTitle>
              {view.row.centre_name} — {monthLabel}
              {view.row.po_number ? ` · ${view.row.po_number}` : ''}
            </DialogTitle>
            <DialogContent>
              <Table size="small" className={classes.fitTable}>
                <TableHead>
                  <TableRow>
                    <TableCell width="6%">Sl No.</TableCell>
                    <TableCell width="13%">Reference ID</TableCell>
                    <TableCell width="27%">Item</TableCell>
                    <TableCell width="8%">Link</TableCell>
                    <TableCell width="9%">Qty</TableCell>
                    <TableCell width="11%">Cost/pc</TableCell>
                    <TableCell width="11%">Total</TableCell>
                    <TableCell width="15%">
                      {view.row.status === 'sent' ||
                      view.row.status === 'received'
                        ? 'Receipt'
                        : 'Remarks'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(view.sheet.items || [])
                    .filter((it) => Number(it.qty) > 0)
                    .map((it, idx) => (
                      <TableRow key={it.catalogue_item}>
                        <TableCell className={classes.muted}>
                          {idx + 1}
                        </TableCell>
                        <TableCell className={classes.muted}>
                          {it.reference_id || '—'}
                        </TableCell>
                        <TableCell className={classes.wrap}>
                          {it.name}
                        </TableCell>
                        <TableCell>
                          <ItemLink link={it.reference_link} />
                        </TableCell>
                        <TableCell>{Number(it.qty)}</TableCell>
                        <TableCell>{inr(it.unit_price || it.cost)}</TableCell>
                        <TableCell>{inr(it.line_total)}</TableCell>
                        <TableCell
                          className={`${classes.muted} ${classes.wrap}`}
                        >
                          {view.row.status === 'sent' ||
                          view.row.status === 'received' ? (
                            <>
                              <span
                                style={{
                                  fontWeight: 600,
                                  color:
                                    it.receipt_status === 'received'
                                      ? '#2e7d32'
                                      : it.receipt_status === 'pending'
                                      ? '#8d6e00'
                                      : it.receipt_status === 'closed'
                                      ? '#616161'
                                      : it.receipt_status === 'unavailable'
                                      ? '#8a3d00'
                                      : '#c62828'
                                }}
                              >
                                {RECEIPT_LABEL[it.receipt_status] || 'Awaiting'}
                              </span>
                              {it.receipt_note ? ` — ${it.receipt_note}` : ''}
                              {FLAGGED.includes(it.receipt_status) ? (
                                <div style={{ marginTop: 2 }}>
                                  <Button
                                    size="small"
                                    color="primary"
                                    disabled={acting}
                                    style={{ minWidth: 0, padding: '0 4px' }}
                                    onClick={() =>
                                      resolveLines('reorder', [it.order])
                                    }
                                  >
                                    Reorder
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={acting}
                                    style={{ minWidth: 0, padding: '0 4px' }}
                                    onClick={() =>
                                      resolveLines('close_issue', [it.order])
                                    }
                                  >
                                    Close
                                  </Button>
                                </div>
                              ) : null}
                              {it.receipt_status === 'unavailable' ? (
                                <div style={{ marginTop: 2 }}>
                                  <Button
                                    size="small"
                                    color="primary"
                                    disabled={acting}
                                    style={{ minWidth: 0, padding: '0 4px' }}
                                    title="Back in stock — order it after all"
                                    onClick={() =>
                                      resolveLines('reorder', [it.order])
                                    }
                                  >
                                    Reorder
                                  </Button>
                                </div>
                              ) : null}
                              {it.procurement_remarks ? (
                                <div
                                  style={{
                                    fontSize: 11,
                                    whiteSpace: 'pre-line'
                                  }}
                                >
                                  {it.procurement_remarks}
                                </div>
                              ) : null}
                            </>
                          ) : (
                            it.centre_remarks || '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  {!(view.sheet.items || []).some(
                    (it) => Number(it.qty) > 0
                  ) && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="textSecondary">
                          No lines for this centre this month.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow className={classes.totalRow}>
                    <TableCell>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell>{inr(view.sheet.total)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
              {Number(view.sheet.flagged_lines) > 0 ? (
                <TextField
                  label="Note to the centre (goes with Reorder / Close)"
                  placeholder="e.g. replacement ordered from Amazon, due next week"
                  fullWidth
                  size="small"
                  variant="outlined"
                  style={{ marginTop: 12 }}
                  value={issueNote}
                  onChange={(e) => setIssueNote(e.target.value)}
                />
              ) : null}
            </DialogContent>
            <DialogActions>
              {Number(view.sheet.flagged_lines) > 1 ? (
                <Button
                  size="small"
                  color="primary"
                  disabled={acting}
                  onClick={() => resolveLines('reorder', [])}
                >
                  Reorder all flagged
                </Button>
              ) : null}
              <Button size="small" onClick={() => exportRow(view.row, 'excel')}>
                Excel
              </Button>
              <Button size="small" onClick={() => exportRow(view.row, 'pdf')}>
                PDF
              </Button>
              <span style={{ flex: 1 }} />
              <Button size="small" onClick={() => setView(null)}>
                Close
              </Button>
              {view.row.status === 'submitted' && (
                <Button
                  size="small"
                  disabled={acting}
                  onClick={() => setReject(view.row)}
                >
                  Send back
                </Button>
              )}
              {view.row.status === 'submitted' && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={acting}
                  onClick={() => confirmAct(view.row, 'approve')}
                >
                  Approve
                </Button>
              )}
              {view.row.status === 'approved' && (
                <Button
                  size="small"
                  disabled={acting}
                  onClick={() => confirmAct(view.row, 'reopen')}
                >
                  Reopen
                </Button>
              )}
              {view.row.status === 'approved' && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={acting}
                  onClick={() => openOrder(view.row)}
                >
                  Mark ordered
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!ordering}
        onClose={() => setOrdering(null)}
        maxWidth="md"
        fullWidth
      >
        {ordering && (
          <>
            <DialogTitle>
              Mark ordered — {ordering.row.centre_name} · {monthLabel}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Everything starts as <b>Ordered</b>. Switch an item to{' '}
                <b>Unavailable</b> if you couldn’t buy it, and add a short
                reason — the centre sees it and won’t wait for that item.
              </Typography>
              <Table size="small" className={classes.fitTable}>
                <TableHead>
                  <TableRow>
                    <TableCell width="34%">Item</TableCell>
                    <TableCell width="8%">Qty</TableCell>
                    <TableCell width="12%">Total</TableCell>
                    <TableCell width="20%">Status</TableCell>
                    <TableCell width="26%">Reason (centre sees this)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordering.lines.map((l, i) => (
                    <TableRow key={l.id}>
                      <TableCell className={classes.wrap}>{l.name}</TableCell>
                      <TableCell>{l.qty}</TableCell>
                      <TableCell>{inr(l.total)}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          variant="outlined"
                          fullWidth
                          value={l.unavailable ? 'unavailable' : 'ordered'}
                          onChange={(e) =>
                            updateOrderLine(i, {
                              unavailable: e.target.value === 'unavailable'
                            })
                          }
                        >
                          <MenuItem value="ordered">Ordered</MenuItem>
                          <MenuItem value="unavailable">Unavailable</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          variant="outlined"
                          fullWidth
                          placeholder={
                            l.unavailable ? 'e.g. Out of stock on Amazon' : ''
                          }
                          disabled={!l.unavailable}
                          error={l.unavailable && !l.note.trim()}
                          value={l.note}
                          onChange={(e) =>
                            updateOrderLine(i, { note: e.target.value })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(() => {
                const un = ordering.lines.filter((l) => l.unavailable)
                const sum = (a) => a.reduce((t, l) => t + l.total, 0)
                return (
                  <Typography variant="body2" style={{ marginTop: 12 }}>
                    Ordered:{' '}
                    <b>
                      {ordering.lines.length - un.length} items ·{' '}
                      {inr(sum(ordering.lines) - sum(un))}
                    </b>
                    {un.length ? (
                      <span style={{ color: '#8a3d00' }}>
                        {' '}
                        · Unavailable:{' '}
                        <b>
                          {un.length} items · {inr(sum(un))}
                        </b>{' '}
                        — not counted in the centre’s spend
                      </span>
                    ) : null}
                  </Typography>
                )
              })()}
            </DialogContent>
            <DialogActions>
              <Button size="small" onClick={() => setOrdering(null)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={acting}
                onClick={submitOrder}
              >
                {(() => {
                  const n = ordering.lines.filter((l) => l.unavailable).length
                  return n
                    ? `Mark ordered (${
                        ordering.lines.length - n
                      }) · ${n} unavailable`
                    : 'Mark all ordered'
                })()}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!reject}
        onClose={() => setReject(null)}
        maxWidth="xs"
        fullWidth
      >
        {reject && (
          <>
            <DialogTitle>Send {reject.centre_name} back?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" gutterBottom>
                The month reopens for the centre with your note.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                placeholder="What should they change?"
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button size="small" onClick={() => setReject(null)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={acting}
                onClick={() => act(reject, 'reject', rejectRemarks)}
              >
                Send back
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  )
}
